import React, { useEffect, useState } from 'react';
import {
  Button,
  Badge,
  RadioGroup,
  RadioGroupItem,
  Spinner,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Separator,
  cn,
} from '@rnd-payplus/alloy-react';
import { Rocket, ArrowLeft, ArrowRight, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';
import { usePageShell } from '../contexts/PageShellContext';

const steps = ['Select Tenant', 'Choose Integration Flow', 'Configure Mapping', 'Review & Launch'];

const integrationFlows = [
  {
    id: 'sap-to-pay',
    name: 'SAP F110 to Tungsten Pay+',
    description: 'Process SAP payment runs through Pay Backend',
    source: 'SAP F110',
    target: 'Tungsten Pay+',
    requiredFields: ['documentNumber', 'currency', 'beneficiaryName', 'amount'],
  },
  {
    id: 'sap-to-bank',
    name: 'SAP F110 to Bank Direct',
    description: 'Direct bank file generation from SAP',
    source: 'SAP F110',
    target: 'Bank File',
    requiredFields: ['accountNumber', 'amount', 'currency', 'beneficiaryName'],
  },
  {
    id: 'oracle-to-pay',
    name: 'Oracle Fusion to Tungsten Pay+',
    description: 'Oracle payments through Pay Backend',
    source: 'Oracle Fusion',
    target: 'Tungsten Pay+',
    requiredFields: ['paymentReference', 'currency', 'amount'],
  },
];

const OnboardingWizardPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedFlow, setSelectedFlow] = useState('');
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [createdResources, setCreatedResources] = useState<any[]>([]);

  const loadTenants = async () => {
    try {
      const response = await fetch('/api/tenants');
      if (!response.ok) {
        throw new Error(`Failed to load tenants (${response.status})`);
      }
      setTenants(await response.json());
    } catch (err) {
      setTenants([]);
      toast.error(err instanceof Error ? err.message : 'Failed to load tenants');
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleLaunch = async () => {
    setLoading(true);
    try {
      const flow = integrationFlows.find((f) => f.id === selectedFlow);
      if (!flow) throw new Error('No flow selected');

      const connRes = await fetch('/api/connections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: selectedTenant,
          platform: flow.source.toLowerCase().replace(/\s+/g, '-'),
          name: `${flow.name} — ${flow.source}`,
          status: 'active',
        }),
      });
      const connData = await connRes.json();
      const connectionId = connData.id;

      const mappingRes = await fetch('/api/mappings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: selectedTenant,
          name: `${flow.name} mapping`,
          sourceSystem: flow.source,
          targetSystem: flow.target,
          fieldMappings: flow.requiredFields.map((f: string) => ({ sourceField: f, targetField: f })),
        }),
      });
      const mappingData = await mappingRes.json();
      const mappingId = mappingData.id;

      const flowRes = await fetch('/api/dataflows', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: selectedTenant,
          type: 'integration',
          source: flow.source,
          target: flow.target,
          status: 'active',
          config: { connectionId, mappingId },
        }),
      });
      const flowData = await flowRes.json();
      const dataflowId = flowData.id;

      if (connectionId) {
        await fetch(`/api/connections/${connectionId}/test`, { method: 'POST' }).catch(() => {});
      }

      setCreatedResources([
        { label: 'Connection ID', value: connectionId },
        { label: 'Mapping ID', value: mappingId },
        { label: 'Dataflow ID', value: dataflowId },
      ]);
      setCompleted(true);
    } catch (err) {
      console.error('Launch failed:', err);
      toast.error('Launch failed. Please check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const flow = integrationFlows.find((f) => f.id === selectedFlow);

  usePageShell({
    title: 'Integration Onboarding Wizard',
    primaryAction: activeStep === 3 && !completed ? (
      <Button onClick={handleLaunch} disabled={loading}>
        {loading ? (
          <Spinner className="mr-2 size-4" />
        ) : (
          <Rocket className="mr-2 size-4" />
        )}
        {loading ? 'Setting Up...' : 'Launch Integration'}
      </Button>
    ) : undefined,
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Onboarding' },
    ],
  });

  return (
    <div className="space-y-5">
      {/* Stepper */}
      <div className="flex items-center gap-0">
        {steps.map((step, i) => (
          <React.Fragment key={i}>
            <div className="flex items-center gap-2 shrink-0">
              <div
                className={cn(
                  'flex items-center justify-center size-8 rounded-full border-2 text-sm font-semibold shrink-0',
                  i < activeStep
                    ? 'border-primary bg-primary text-primary-foreground'
                    : i === activeStep
                      ? 'border-foreground text-foreground'
                      : 'border-muted-foreground/30 text-muted-foreground',
                )}
              >
                {i < activeStep ? <CheckCircle className="size-4" /> : i + 1}
              </div>
              <span
                className={cn(
                  'text-sm whitespace-nowrap',
                  i <= activeStep ? 'font-medium text-foreground' : 'text-muted-foreground',
                )}
              >
                {step}
              </span>
            </div>
            {i < steps.length - 1 && (
              <Separator
                className={cn(
                  'flex-1 mx-3',
                  i < activeStep ? 'bg-primary' : 'bg-muted-foreground/30',
                )}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <div>
        {activeStep === 0 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-base font-medium leading-6">Select Tenant</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Choose the tenant/customer you're onboarding to the Connectivity Hub
              </p>
            </div>
            <Select value={selectedTenant} onValueChange={setSelectedTenant}>
              <SelectTrigger className="w-full max-w-sm">
                <SelectValue placeholder={tenants.length === 0 ? 'No tenants — create one in Tenants first' : 'Select a Tenant'} />
              </SelectTrigger>
              <SelectContent>
                {tenants.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {activeStep === 1 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-base font-medium leading-6">Choose Integration Flow</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Select the integration flow you want to set up for this tenant
              </p>
            </div>
            <RadioGroup value={selectedFlow} onValueChange={setSelectedFlow}>
              {integrationFlows.map((f) => (
                <label
                  key={f.id}
                  htmlFor={f.id}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors',
                    selectedFlow === f.id
                      ? 'border-primary bg-accent/50'
                      : 'hover:bg-accent/30',
                  )}
                >
                  <RadioGroupItem value={f.id} id={f.id} className="mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">{f.name}</p>
                    <p className="text-sm text-muted-foreground">{f.description}</p>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>
        )}

        {activeStep === 2 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-base font-medium leading-6">Configure Mapping</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Select the integration flow you want to set up for this tenant
              </p>
            </div>

            <div className="flex items-start gap-3 text-sm">
              <Info className="size-5 text-muted-foreground shrink-0 mt-0.5" />
              <div className="space-y-3">
                <p className="text-muted-foreground">The wizard will automatically:</p>
                {[
                  'Detect required fields',
                  'Create transformation mapping',
                  'Configure source connection',
                  'Configure target connection',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle className="size-5 text-green-600 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-2">
                Required Fields for {flow?.target}:
              </h3>
              <div className="flex gap-1.5 flex-wrap">
                {flow?.requiredFields.map((field) => (
                  <Badge key={field} variant="secondary">
                    {field}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeStep === 3 && !completed && (
          <div className="space-y-5">
            <div>
              <h2 className="text-base font-medium leading-6">Review & Launch</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Ready to create all resources for this integration
              </p>
            </div>

            <div className="space-y-3">
              <div className="rounded-lg border p-4">
                <p className="text-sm font-semibold">Tenant</p>
                <p className="text-sm text-muted-foreground">
                  {tenants.find((t) => t.id === selectedTenant)?.name || 'Acme Corporation'}
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm font-semibold">Integration Flow</p>
                <p className="text-sm text-muted-foreground">{flow?.name}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-lg border p-4 text-sm">
              <AlertTriangle className="size-5 text-muted-foreground shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">This will create:</p>
                <ul className="mt-1 space-y-0.5 text-muted-foreground">
                  <li>1 Transformation Mapping</li>
                  <li>2 Connections (Source + Target)</li>
                  <li>1 Test Data Flow</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeStep === 3 && completed && (
          <div className="space-y-5">
            <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
              <CheckCircle className="size-5 text-green-600 shrink-0" />
              <p className="text-sm font-medium text-green-800">
                Integration successfully configured!
              </p>
            </div>

            <div>
              <h3 className="text-base font-medium mb-3">Created Resources</h3>
              <div className="space-y-2">
                {createdResources.map((resource, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="size-5 text-green-600 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{resource.label}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {resource.value ?? '---'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => (window.location.href = '/integrations')}>
                Go to Integrations
              </Button>
              <Button variant="outline" onClick={() => (window.location.href = '/data-flows')}>
                Go to Data Flows
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      {!completed && (
        <div className="flex justify-between pt-2">
          <Button variant="outline" disabled={activeStep === 0} onClick={handleBack}>
            <ArrowLeft className="mr-2 size-4" />
            Back
          </Button>
          {activeStep < steps.length - 1 && (
            <Button
              onClick={handleNext}
              disabled={
                (activeStep === 0 && !selectedTenant) || (activeStep === 1 && !selectedFlow)
              }
            >
              Next
              <ArrowRight className="ml-2 size-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default OnboardingWizardPage;
